import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import DefaultButton from 'components/Utils/NewDefaultButton';
import moment from 'moment-timezone';
import CalendarUtils from 'services/CalendarUtils';
import MenuItem from '@material-ui/core/MenuItem';
import PerformanceUtils from 'services/PerformanceUtils';
import PhysicianUtils from 'services/PhysicianUtils';
import StaffUtils from 'services/StaffUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppSelector } from 'careand-redux/store';
import PatientUtils from 'services/PatientUtils';
import PatientSearchField from 'components/Patient/PatientSearchField';
import DefaultTextField from 'components/Utils/DefaultTextField';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import DefaultDatePicker from 'components/Utils/DefaultDatePicker';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import ClinicUtils from 'services/ClinicUtils';

export const TIME_OFF_REASONS = [
  'Personal Appointment',
  'Patient',
  'Admin Time',
  'Lunch & Learn',
  'Team Meeting',
];

const bookTimeOffSchema = z.object({
  physician_id: z.string().min(1, 'Must choose a practitioner'),
  clinic_id: z.string().optional(),
  date: z.any().refine((val) => !!val, 'Must choose a date'),
  date_start: z.any().refine((val) => !!val, 'Must choose a time'),
  date_end: z.any().refine((val) => !!val, 'Must choose a time'),
  reason: z.string().min(1, 'Must type a reason'),
  details: z.string().optional().or(z.literal('')),
});
type BookTimeOffValues = z.infer<typeof bookTimeOffSchema>;

const BookTimeOff = ({
  physician,
  onClose,
  startTime,
  clinicId,
}: {
  physician?: any;
  onClose?: () => void;
  startTime?: number;
  clinicId?: string;
}) => {
  const dispatch = useAppDispatch();

  const physicians = useAppSelector((state) => state.physicians.byId);
  const clinics = useAppSelector((state) => state.resource.clinics);

  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState<any>(null);

  const startDate = startTime || moment().unix();
  const endDate = startDate + 30 * 60;

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
  } = useForm<BookTimeOffValues>({
    resolver: zodResolver(bookTimeOffSchema),
    defaultValues: {
      date: startDate,
      date_start: startDate,
      date_end: endDate,
      physician_id: physician ? physician.id : '',
      clinic_id:
        clinicId || ClinicUtils.resolveForwardedClinicId(physician?.clinic_id, clinics) || '',
      reason: '',
      details: '',
    },
    mode: 'onChange',
  });

  const reason = watch('reason');
  const physicianId = watch('physician_id');
  const clinicIdValue = watch('clinic_id');

  const selectedPhysician = physicianId ? physicians[physicianId] : null;
  const physicianClinicId = selectedPhysician?.clinic_id;

  useEffect(() => {
    if (physicianClinicId) {
      // A profile can still name a closed clinic; time off goes where they work.
      setValue(
        'clinic_id',
        ClinicUtils.resolveForwardedClinicId(physicianClinicId, clinics) || physicianClinicId,
      );
    }
  }, [physicianClinicId, setValue, clinics]);

  async function book(values: BookTimeOffValues) {
    let { date, date_start, date_end, physician_id, clinic_id, reason, details } = values;

    physician_id = physician_id || physician?.id;

    let dates = convertDates(date, date_start, date_end);
    date_start = dates.date_start;
    date_end = dates.date_end;

    if (reason === 'Patient' && patient) {
      reason += ` - ${PatientUtils.getName(patient, undefined, false)}`;
    } else if (details) {
      reason += ` - ${details}`;
    }

    let timeOff = { date_start, date_end, reason, clinic_id };

    setLoading(true);
    let result = await dispatch(api.Resource.bookTimeOff({ physicianId: physician_id, timeOff }), {
      errorTitle: 'Book Time Off',
    });
    setLoading(false);
    if (result && onClose) onClose();
  }

  const activePhysicians = StaffUtils.filterActiveUsers(
    _.compact(PerformanceUtils.objectToArray(physicians)),
  );

  return (
    <form onSubmit={handleSubmit(book)} className="book-time-off" style={{ width: '400px' }}>
      <DefaultSelectField
        style={{ width: '100%' }}
        name="physician_id"
        control={control}
        disabled={physician !== undefined}
        label="Practitioner"
        inputElements={activePhysicians.map((physician) => (
          <MenuItem value={physician.id} key={physician.id}>
            {PhysicianUtils.getName(physician)}
          </MenuItem>
        ))}
      />
      {clinics.length > 1 && (
        <DefaultSelectField
          style={{ width: '100%' }}
          name="clinic_id"
          control={control}
          label="Clinic"
          inputElements={ClinicUtils.getSelectableClinics(clinics, clinicIdValue).map((clinic) => (
            <MenuItem value={clinic.id} key={clinic.id}>
              {clinic.name}
            </MenuItem>
          ))}
        />
      )}
      <DefaultDatePicker
        name="date"
        control={control}
        label="Day"
        style={{
          width: '100%',
          display: 'inline-block',
        }}
      />
      <div className="row">
        <StaffTimePicker
          name="date_start"
          control={control}
          label="Start"
          style={{
            width: 'calc(50% - 10px)',
            marginRight: '20px',
            display: 'inline-block',
          }}
        />
        <StaffTimePicker
          name="date_end"
          control={control}
          label="End"
          style={{
            width: 'calc(50% - 10px)',
            display: 'inline-block',
          }}
        />
      </div>
      <DefaultSelectField
        name="reason"
        control={control}
        label="Reason"
        style={{ width: '100%' }}
        inputs={TIME_OFF_REASONS}
      />
      {reason === 'Patient' ? (
        <PatientSearchField onSelect={setPatient} />
      ) : (
        <DefaultTextField
          name="details"
          control={control}
          label="Details"
          style={{ width: '100%' }}
        />
      )}
      <div className="row" style={{ textAlign: 'center' }}>
        <DefaultButton
          name="Submit"
          disabled={!isValid}
          style={{ margin: '15px auto', width: '200px' }}
          loading={loading}
        />
      </div>
    </form>
  );
};

function convertDates(date: any, date_start: any, date_end: any) {
  let isCorrectOrder = date_start < date_end;

  date_start = CalendarUtils.joinDateAndTime(date, date_start);
  date_end = CalendarUtils.joinDateAndTime(date, date_end);

  let isCorrectOrder2 = date_start < date_end;

  if (isCorrectOrder && !isCorrectOrder2) {
    date_end = moment.unix(date_end).add(24, 'hours').unix();
  }
  return { date_start, date_end };
}

export default BookTimeOff;
